/**
 * The parts of sign-up that the console's sign-in page needs (so the sign-up page itself loads only when opened):
 * the public sign-up settings and the "check your inbox" card.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useT } from '@/i18n';

export interface SignupConfig { enabled: boolean; mode?: 'open' | 'approval' | 'closed'; terms?: string; registration?: Record<string, string>; subscriberRanges?: string[]; accessTypes?: string[] }

/** The public sign-up settings (open / approval / closed). */
export function useSignupConfig() {
  const [cfg, setCfg] = useState<SignupConfig | null>(null);
  useEffect(() => { axios.get('/api/core/signup/config').then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  return cfg;
}

/** After sign-up (and from the login page): check the inbox, or get the link again. */
export function CheckEmail({ email, mail }: { email: string; mail?: string }) {
  const t = useT();
  const [sent, setSent] = useState(false); const [busy, setBusy] = useState(false);
  const resend = async () => { setBusy(true); try { await axios.post('/api/core/signup/resend', { email }); setSent(true); } catch { /* same answer either way */ setSent(true); } finally { setBusy(false); } };
  return (
    <div className="rounded-2xl border bg-card p-6 text-center shadow-card">
      <MailCheck className="mx-auto size-10 text-primary" />
      <h1 className="mt-3 text-xl font-bold">{t('signup.check_title')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('signup.check_body', { email })}</p>
      {mail && mail !== 'sent' && <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">{t('signup.mail_delayed')}</p>}
      <div className="mt-5 flex flex-col items-center gap-2">
        <Button variant="outline" disabled={busy || sent} onClick={resend}>{busy && <Loader2 className="size-4 animate-spin" />}{sent ? t('signup.resent') : t('signup.resend')}</Button>
        <Link to="/login" className="text-sm font-semibold text-primary">{t('signup.back_to_login')}</Link>
      </div>
    </div>
  );
}
